import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import customDate from "../../../utils/customDate";
import axios from "axios";
import {
  ArrowBack,
  CheckCircle,
  Close,
  CreditCard,
  Info,
} from "@mui/icons-material";
import RenderSeatMap from "./RenderSeatMap";
import {
  AccessTime,
  Chair,
  FlightLand,
  FlightTakeoff,
  Luggage,
  MonetizationOn,
  Refresh,
  Restaurant,
  Shield,
} from "@mui/icons-material";
import customTime from "../../../utils/customTime";
import {
  getAirArabiaPricing,
  getAirArabiaPricingBundle,
  getBaggageDetails,
  getMealDetails,
  getSeatDetails,
} from "../Service/AirarabiaApi";
import Loading from "../../Loading";
import { formatNumber } from "../../../utils/convertToNumber";
import { toast } from "react-toastify";
import Ancillaries from "./components/Ancillaries";

const PriceWithBundle = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [bundlePricing, setBundlePricing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const passengersData = location.state?.passengersData;
  const originalPath = location.state?.originalPath;
  const selectedBundle = location.state?.selectedBundle;
  const passengers = location.state?.passengers;
  const pricingResponse = location.state?.response;
  const selectedOutboundBundle = location.state?.selectedOutboundBundle;
  const selectedInboundBundle = location.state?.selectedInboundBundle;
  const pricingRequest = location.state?.pricingRequest;
  const queryURL = location.state?.queryURL;
  const charges = location.state?.charges;
  const currency = location.state?.currency;

  const [isBaggageModalOpen, setIsBaggageModalOpen] = useState(false);
  const [baggageDetails, setBaggageDetails] = useState(null);
  const [selectedBaggage, setSelectedBaggage] = useState({});

  const [isMealModalOpen, setIsMealModalOpen] = useState(false);
  const [mealDetails, setMealDetails] = useState(null);
  const [selectedMeals, setSelectedMeals] = useState({});

  const [isSeatModalOpen, setIsSeatModalOpen] = useState(false);
  const [seatDetails, setSeatDetails] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState({});

  const [activePassengerTab, setActivePassengerTab] = useState(0);
  const [activeSeatTab, setActiveSeatTab] = useState(0);

  const [pricing, setPricing] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const [autoSelectedSeats, setAutoSelectedSeats] = useState(false);
  const [activeMealSegmentTab, setActiveMealSegmentTab] = useState(0);

  useEffect(() => {
    // if (!searchParams || !selectedBundle) {
    //   navigate("/");
    //   return;
    // }
    fetchBundlePricing();
  }, [
    passengersData,
    selectedBundle,
    selectedOutboundBundle,
    selectedInboundBundle,
    navigate,
  ]);

  useEffect(() => {
    if (pricing) {
      navigate(`/price-with-ancillaries${queryURL}`, {
        state: {
          passengersData,
          originalPath,
          response: pricing,
          sessionInfo: pricing?.sessionInfo,
          selectedBundle,
          selectedOutboundBundle,
          selectedInboundBundle,
          queryURL,
          charges: charges || 0,
          currency,
        },
      });
    }
  }, [pricing]);

  const fetchBundlePricing = async () => {
    const bundleRequest = {
      segments: pricingResponse?.flights?.map((segment) => ({
        departureDateTime: segment.departureDateTime,
        arrivalDateTime: segment.arrivalDateTime,
        departureAirport: {
          locationCode: segment.departureAirport?.code,
          terminal: segment.departureAirport?.terminal || "",
        },
        arrivalAirport: {
          locationCode: segment.arrivalAirport?.code,
          terminal: segment.arrivalAirport?.terminal || "",
        },
        flightNumber: segment.flightNumber,
        rph: segment.rph,
        returnFlag: "false",
        operatingAirline: {
          code: segment?.rph?.slice(0, 2),
        },
        directionInd: originalPath.length > 1 ? "Return" : "OneWay",
      })),
      passengers: {
        ADT: passengersData.ADT,
        CHD: passengersData.CHD,
        INF: passengersData.INF,
      },
      bundleServices: {
        outboundBundleId:
          originalPath?.length > 1
            ? selectedOutboundBundle?.id
            : selectedBundle?.id,
        ...(selectedInboundBundle?.id && {
          inboundBundleId: selectedInboundBundle?.id,
        }),
      },
      transactionIdentifier: pricingResponse?.transactionIdentifier,
      jsessionId: pricingResponse?.sessionInfo?.jsessionId,
    };

    const response = await getAirArabiaPricingBundle(
      bundleRequest,
      setIsLoading
    );

    if (response?.data?.success) {
      setBundlePricing(response?.data?.data);
    } else {
      setError("Failed to fetch bundle pricing data");
    }
  };

  const fetchPricingWithAncillaries = async () => {
    setIsLoading(true);

    const ancillaries = {
      baggage: [],
      meals: [],
      seats: [],
    };

    Object.entries(selectedBaggage).forEach(([, segments]) => {
      Object.entries(segments).forEach(([, baggage]) => {
        ancillaries.baggage.push({
          baggageCode: baggage.baggageCode,
          TravelerRefNumberRPHList: baggage.TravelerRefNumberRPHList,
          FlightRefNumberRPHList: baggage.rph,
          DepartureDate: baggage.DepartureDate,
          FlightNumber: baggage.FlightNumber,
        });
      });
    });

    // Process meals for all passengers
    Object.entries(selectedMeals).forEach(([, segments]) => {
      Object.entries(segments).forEach(([, meal]) => {
        ancillaries.meals.push({
          mealCode: meal.mealCode,
          mealQuantity: meal.mealQuantity || 1,
          TravelerRefNumberRPHList: meal.TravelerRefNumberRPHList,
          FlightRefNumberRPHList: pricingResponse.flights
            ?.map(
              (flight) =>
                flight?.flightNumber === meal.FlightNumber && flight?.rph
            )
            .filter(Boolean)[0],
          DepartureDate: meal.DepartureDate,
          FlightNumber: meal.FlightNumber,
        });
      });
    });

    // Process seats for all passengers
    Object.entries(selectedSeats).forEach(([, segments]) => {
      Object.entries(segments).forEach(([, seat]) => {
        ancillaries.seats.push({
          SeatNumber: seat.seatId,
          TravelerRefNumberRPHList: seat.TravelerRefNumberRPHList,
          FlightRefNumberRPHList: pricingResponse.flights
            ?.map(
              (flight) =>
                flight?.flightNumber === seat.FlightNumber && flight?.rph
            )
            .filter(Boolean)[0],
          DepartureDate: seat.DepartureDate,
          FlightNumber: seat.FlightNumber,
        });
      });
    });

    const requestData = {
      ...pricingRequest,
      passengers: {
        ADT: passengersData.ADT,
        CHD: passengersData.CHD,
        INF: passengersData.INF,
      },
      ancillaries: ancillaries,
      bundleServices: {
        outboundBundleId:
          originalPath.length > 1
            ? selectedOutboundBundle.id
            : selectedBundle.id,
        ...(selectedInboundBundle?.id && {
          inboundBundleId: selectedInboundBundle.id,
        }),
      },
      transactionIdentifier: pricingResponse?.transactionIdentifier,
      jsessionId: pricingResponse?.sessionInfo?.jsessionId,
      type: "ancillaries",
    };

    const response = await getAirArabiaPricing(requestData, setIsLoading);

    if (response.data.success) {
      setPricing(response.data);
    } else {
      setError("Failed to fetch updated pricing data");
    }
  };

  const getSegmentPath = (segment) => {
    const departureCode =
      segment?.flightSegment?.departureAirport?.locationCode ||
      segment?.flightSegment?.departureAirport?.code ||
      segment?.flightSegment?.origin ||
      "N/A";

    const arrivalCode =
      segment?.flightSegment?.arrivalAirport?.locationCode ||
      segment?.flightSegment?.arrivalAirport?.code ||
      segment?.flightSegment?.destination ||
      "N/A";

    return `${departureCode}-${arrivalCode}`;
  };

  const isAllBaggageSelected = () => {
    const segments = bundlePricingResp?.pricedItineraries?.[0]?.segments || [];
    return passengers?.every((passenger, passengerIndex) =>
      segments.every(
        (_, segmentIndex) =>
          selectedBaggage[passengerIndex]?.[segmentIndex]?.baggageCode
      )
    );
  };

  const isAllMealsSelected = () => {
    const segments = bundlePricingResp?.pricedItineraries?.[0]?.segments || [];
    return passengers?.every((passenger, passengerIndex) =>
      segments.every(
        (_, segmentIndex) =>
          selectedMeals[passengerIndex]?.[segmentIndex]?.mealCode
      )
    );
  };

  const isAllSeatsSelected = () => {
    const segments = bundlePricingResp?.pricedItineraries?.[0]?.segments || [];
    return passengers?.every((passenger, passengerIndex) =>
      segments.every(
        (_, segmentIndex) =>
          selectedSeats[passengerIndex]?.[segmentIndex]?.seatId
      )
    );
  };

  const handleProceedToBooking = () => {
    const errors = [];
    const segments = bundlePricingResp?.pricedItineraries?.[0]?.segments || [];

    passengers?.forEach((passenger, passengerIndex) => {
      segments.forEach((segment, segmentIndex) => {
        const segmentPath = getSegmentPath(segment);

        // Check baggage selection
        if (!selectedBaggage[passengerIndex]?.[segmentIndex]?.baggageCode) {
          errors.push(
            `Passenger ${
              passengerIndex + 1
            } - ${segmentPath}: Please select baggage`
          );
        }

        // Check meal selection
        if (!selectedMeals[passengerIndex]?.[segmentIndex]?.mealCode) {
          errors.push(
            `Passenger ${
              passengerIndex + 1
            } - ${segmentPath}: Please select a meal`
          );
        }

        // Check seat selection
        if (!selectedSeats[passengerIndex]?.[segmentIndex]?.seatId) {
          errors.push(
            `Passenger ${
              passengerIndex + 1
            } - ${segmentPath}: Please select a seat`
          );
        }
      });
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setValidationErrors([]);

    fetchPricingWithAncillaries();
    if (pricing) {
      navigate(`/price-with-ancillaries${queryURL}`, {
        state: {
          passengersData,
          originalPath,
          response: pricing,
          sessionInfo: pricing?.sessionInfo,
          selectedBundle,
          selectedOutboundBundle,
          selectedInboundBundle,
          queryURL,
          charges: charges || 0,
          currency,
        },
      });
    }
  };

  const getBundleIcon = (service) => {
    switch (service) {
      case "BAGGAGE":
        return <Luggage className="w-4 h-4" />;
      case "SEAT_MAP":
        return <CheckCircle className="w-4 h-4" />;
      case "MEAL":
        return <Restaurant className="w-4 h-4" />;
      case "FLEXI_CHARGES":
        return <Shield className="w-4 h-4" />;
      default:
        return <Shield className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount, currency) => {
    if (amount === 0 || amount < 0.01) {
      return "Free";
    }
    return `${currency} ${amount?.toFixed(2)}`;
  };

  const totalTax = pricingResponse?.pricing?.passengers
    ?.reduce((acc, item) => {
      const taxSum =
        item.taxes?.reduce((taxAcc, tax) => taxAcc + Number(tax.amount), 0) ||
        0;
      const quantity = item.passengerType?.quantity || 1;
      return Number(acc + taxSum * quantity);
    }, 0)
    .toFixed(2);

  if (isLoading) {
    return <Loading />;
  }

  if (error || !bundlePricing?.pricedItineraries) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-medium text-gray-800">
            {error || "Unable to load bundle pricing"}
          </h2>
          <p className="mb-4 text-gray-600">Please try again later.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 text-white rounded-lg transition-colors bg-main hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const bundlePricingResp = bundlePricing;

  const flightSegments = bundlePricingResp.pricedItineraries?.[0]?.segments;
  const bundleServices =
    bundlePricingResp?.pricedItineraries?.[0]?.bundleServices || [];
  const pricingInfo = bundlePricingResp.pricedItineraries?.[0]?.pricing;

  const selectedBundleInfo =
    bundleServices.find(
      (bundle) =>
        bundle.bundleId ===
          (originalPath.length > 1
            ? selectedOutboundBundle.id
            : selectedBundle.id) && bundle.applicableOndSequence === "0"
    ) || selectedBundle;

  const selectedInboundBundleInfo =
    bundleServices.find(
      (bundle) =>
        originalPath.length > 1 &&
        bundle.bundleId === selectedInboundBundle.id &&
        bundle.applicableOndSequence === "1"
    ) || selectedInboundBundle;

  const selectedBundles = [
    selectedBundleInfo,
    selectedInboundBundleInfo,
  ].filter((bundle) => bundle !== null && bundle !== undefined);

  // const selectedBundles = [
  //   selectedOutboundBundle,
  //   selectedInboundBundle,
  // ].filter((bundle) => bundle !== null);

  const bundleFee = pricingInfo?.fareBreakdowns?.[0]?.fees?.find(
    (fee) =>
      fee.feeCode
        ?.toLowerCase()
        .includes(
          selectedBundle?.id?.toLowerCase() ||
            selectedOutboundBundle?.id?.toLowerCase() ||
            selectedInboundBundle?.id?.toLowerCase()
        ) ||
      fee.feeCode
        ?.toLowerCase()
        .includes(
          selectedBundle?.name?.toLowerCase() ||
            selectedOutboundBundle?.name?.toLowerCase() ||
            selectedInboundBundle?.name?.toLowerCase()
        ) ||
      fee.feeCode?.toLowerCase().includes("extra") ||
      fee.feeCode?.toLowerCase().includes("ultimate") ||
      fee.feeCode?.toLowerCase().includes("value")
  );

  const fetchBaggageDetails = async () => {
    const request = {
      flightSegments: bundlePricingResp?.pricedItineraries?.[0]?.segments.map(
        (flight) => ({
          travelerRefNumberRPHs: "A1",
          flightNumber: flight?.flightSegment?.flightNumber,
          departureDateTime: flight?.flightSegment?.departureDateTime,
          arrivalDateTime: flight?.flightSegment?.arrivalDateTime,
          rph: flight?.flightSegment?.rph,
          returnFlag: "false",
          departureAirport: flight?.flightSegment?.departureAirport,
          arrivalAirport: flight?.flightSegment?.arrivalAirport,
          operatingAirline: { code: "G9" },
        })
      ),
      transactionIdentifier: bundlePricingResp?.transactionIdentifier,
      jsessionId:
        pricingResponse?.sessionInfo?.jsessionId ||
        bundlePricingResp?.jsessionId,
    };

    const response = await getBaggageDetails(request, setIsLoading);
    console.log({ response });
    if (response.data.success) {
      setBaggageDetails(response.data.baggageDetails);
    }
  };

  const fetchMealDetails = async () => {
    const request = {
      flightSegments: bundlePricingResp?.pricedItineraries?.[0]?.segments.map(
        (flight) => ({
          travelerRefNumberRPHs: "A1",
          flightNumber: flight?.flightSegment?.flightNumber,
          departureDateTime: flight?.flightSegment?.departureDateTime,
          arrivalDateTime: flight?.flightSegment?.arrivalDateTime,
          rph: flight?.flightSegment?.rph,
          returnFlag: "false",
          departureAirport: flight?.flightSegment?.departureAirport,
          arrivalAirport: flight?.flightSegment?.arrivalAirport,
          operatingAirline: { code: "G9" },
        })
      ),
      transactionIdentifier: bundlePricingResp?.transactionIdentifier,
      jsessionId:
        pricingResponse?.sessionInfo?.jsessionId ||
        bundlePricingResp?.jsessionId,
    };
    const response = await getMealDetails(request, setIsLoading);
    if (response.data.success) {
      setMealDetails(response.data.mealDetails);
    }
  };

  const fetchSeatDetails = async () => {
    const request = {
      flightSegments: bundlePricingResp?.pricedItineraries?.[0]?.segments.map(
        (flight) => ({
          travelerRefNumberRPHs: "A1",
          flightNumber: flight?.flightSegment?.flightNumber,
          departureDateTime: flight?.flightSegment?.departureDateTime,
          arrivalDateTime: flight?.flightSegment?.arrivalDateTime,
          rph: flight?.flightSegment?.rph,
          returnFlag: "false",
          departureAirport: flight?.flightSegment?.departureAirport,
          arrivalAirport: flight?.flightSegment?.arrivalAirport,
          operatingAirline: { code: "G9" },
        })
      ),
      transactionIdentifier: bundlePricingResp?.transactionIdentifier,
      jsessionId:
        pricingResponse?.sessionInfo?.jsessionId ||
        bundlePricingResp?.jsessionId,
    };
    const response = await getSeatDetails(request, setIsLoading);
    if (response.data.success) {
      setSeatDetails(response.data);
    }
  };

  const totalPrice = () => {
    const total = pricingInfo?.totalFare?.totalFare?.amount + (charges || 0);

    return Number(total.toFixed(2));
  };

  return (
    <div className="px-4 py-2 min-h-screen">
      <div className="mx-auto mt-2 max-w-6xl">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 transition-colors text-main hover:text-main-800"
        >
          <ArrowBack className="w-5 h-5" />
          <span>Back to Flight Details</span>
        </button>

        {/* Header */}
        <div className="mb-2 text-center">
          <h1 className="mb-2 text-3xl font-semibold text-gray-900">
            Bundle Pricing Confirmation
          </h1>
          <p className="text-lg text-gray-600">
            Review your selected bundle and pricing details
          </p>
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="p-4 mb-6 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="w-5 h-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Please complete your selections
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <ul className="pl-5 space-y-1 list-disc">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="space-y-6 lg:col-span-2">
            {/* Flight Summary */}
            <div className="px-6 py-3 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <h2 className="flex items-center mb-2 text-xl font-semibold text-gray-900">
                <FlightTakeoff className="mr-2 w-5 h-5 text-main" />
                Flight Summary
              </h2>

              {flightSegments?.map((flight, index) => (
                <div key={index} className="px-4 py-2 rounded-lg last:mb-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        Flight {flight?.flightSegment?.flightNumber}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {flight?.flightSegment?.departureDateTime &&
                          customDate(flight?.flightSegment?.departureDateTime)}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">
                        {flight?.flightSegment?.departureAirport
                          ?.locationCode ||
                          flight?.flightSegment?.departureAirport?.code ||
                          flight?.flightSegment?.origin}{" "}
                        →{" "}
                        {flight?.flightSegment?.arrivalAirport?.locationCode ||
                          flight?.flightSegment?.arrivalAirport?.code ||
                          flight?.flightSegment?.destination}
                      </div>
                      {flight?.flightSegment?.departureDateTime &&
                        flight?.flightSegment?.arrivalDateTime && (
                          <div className="text-lg font-medium text-main">
                            {customTime(
                              flight?.flightSegment?.departureDateTime
                            )}{" "}
                            -{" "}
                            {customTime(flight?.flightSegment?.arrivalDateTime)}
                          </div>
                        )}
                      {flight?.flightSegment?.departureAirport?.terminal && (
                        <div className="text-xs text-gray-500">
                          Terminal:{" "}
                          {flight?.flightSegment?.departureAirport?.terminal}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <h3 className="flex items-center mb-6 text-xl font-semibold text-gray-900">
                <Luggage className="mr-2 w-5 h-5 text-main-900" />
                Add Ancillaries
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <button
                  onClick={() => {
                    setIsBaggageModalOpen(true);
                    fetchBaggageDetails();
                    setActivePassengerTab(0);
                  }}
                  className={`flex justify-center items-center px-4 py-3 rounded-lg transition-colors ${
                    isAllBaggageSelected()
                      ? "text-green-800 bg-green-100 hover:bg-green-200"
                      : "text-purple-800 bg-purple-100 hover:bg-purple-200"
                  }`}
                >
                  <Luggage className="mr-2 w-5 h-5" />
                  Add Baggage
                  {isAllBaggageSelected() && (
                    <CheckCircle className="ml-2 w-5 h-5 text-green-600" />
                  )}
                </button>
                <button
                  onClick={() => {
                    setIsMealModalOpen(true);
                    fetchMealDetails();
                    setActivePassengerTab(0);
                  }}
                  className={`flex justify-center items-center px-4 py-3 rounded-lg transition-colors ${
                    isAllMealsSelected()
                      ? "text-green-800 bg-green-100 hover:bg-green-200"
                      : "text-orange-800 bg-orange-100 hover:bg-orange-200"
                  }`}
                >
                  <Restaurant className="mr-2 w-5 h-5" />
                  Add Meals
                  {isAllMealsSelected() && (
                    <CheckCircle className="ml-2 w-5 h-5 text-green-600" />
                  )}
                </button>
                <button
                  onClick={() => {
                    setIsSeatModalOpen(true);
                    fetchSeatDetails();
                    setActiveSeatTab(0);
                  }}
                  className={`flex justify-center items-center px-4 py-3 rounded-lg transition-colors ${
                    isAllSeatsSelected()
                      ? "text-green-800 bg-green-100 hover:bg-green-200"
                      : "text-indigo-800 bg-indigo-100 hover:bg-indigo-200"
                  }`}
                >
                  <Chair className="mr-2 w-5 h-5" />
                  Select Seats
                  {isAllSeatsSelected() && (
                    <CheckCircle className="ml-2 w-5 h-5 text-green-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Selected Bundle Details */}
            {selectedBundles.map((bundle, index) => (
              <div
                key={index}
                className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm"
              >
                {bundle?.applicableOndSequence === "1" ? (
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="flex items-center mb-4 text-xl font-semibold text-gray-900">
                      <FlightLand className="mr-2 w-5 h-5 text-main-900" />
                      Selected Inbound Bundle:{" "}
                      {bundle?.bundleName || selectedBundle?.name}
                    </h2>
                  </div>
                ) : (
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="flex items-center mb-4 text-xl font-semibold text-gray-900">
                      <FlightLand className="mr-2 w-5 h-5 text-main-900" />
                      Selected Outbound Bundle:{" "}
                      {bundle?.bundleName || selectedBundle?.name}
                    </h2>
                  </div>
                )}

                <div className="p-6 rounded-lg border bg-amber-50/30 border-main-700">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {bundle?.bundleName || selectedBundle?.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Booking Class:{" "}
                        {bundle?.bookingClasses ||
                          selectedBundle?.bookingClasses}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-semibold text-main-900">
                        +
                        {formatCurrency(
                          bundleFee?.amount ||
                            bundle?.perPaxBundledFee ||
                            bundle?.price,
                          bundleFee?.currency || "ETB"
                        )}
                      </div>
                      <div className="text-sm text-gray-600">Bundle Fee</div>
                    </div>
                  </div>

                  {/* Included Services */}
                  <div className="mb-4">
                    <h4 className="mb-2 text-sm font-medium text-gray-700">
                      Included Services:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {(
                        bundle?.includedServices ||
                        selectedBundle?.includedServices ||
                        []
                      ).map((service, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full text-main-900 bg-main-100"
                        >
                          {getBundleIcon(service)}
                          <span className="ml-1">
                            {service.replace("_", " ")}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Bundle Description */}
                  <div className="text-sm text-gray-600">
                    <h4 className="mb-2 font-medium text-gray-700">
                      What's included:
                    </h4>
                    {(bundle?.description || selectedBundle?.description || "")
                      .split("\n")
                      .map(
                        (line, index) =>
                          line.trim() && (
                            <div key={index} className="flex items-start mb-1">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              <span>{line.trim()}</span>
                            </div>
                          )
                      )}
                  </div>
                </div>
              </div>
            ))}

            {/* Transaction Information */}
            {/* <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <h2 className="flex items-center mb-4 text-xl font-semibold text-gray-900">
                <Shield className="mr-2 w-5 h-5 text-purple-600" />
                Booking Information
              </h2>

              <div className="grid grid-cols-1 gap-4 text-sm text-black md:grid-cols-2">
                {bundlePricingResp.echoToken && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reference:</span>
                    <span className="font-mono text-xs">
                      {bundlePricingResp.echoToken}
                    </span>
                  </div>
                )}
                {bundlePricingResp.transactionIdentifier && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span className="font-mono text-xs">
                      {bundlePricingResp.transactionIdentifier}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Pricing Source:</span>
                  <span className="capitalize">
                    {pricingInfo?.pricingSource || "Published"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Negotiated Fare:</span>
                  <span
                    className={
                      pricingInfo?.totalFare?.negotiatedFare
                        ? "text-main-900"
                        : "text-gray-500"
                    }
                  >
                    {pricingInfo?.totalFare?.negotiatedFare ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </div> */}

            {/* Flexible Fare Benefits */}
            {pricingInfo?.flexiFares?.flexiFares &&
              pricingInfo.flexiFares.flexiFares.length > 0 && (
                <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <h2 className="flex items-center mb-4 text-xl font-semibold text-gray-900">
                    <Refresh className="mr-2 w-5 h-5 text-main" />
                    Flexible Fare Benefits
                  </h2>

                  <div className="flex items-center mb-4 space-x-4">
                    <div className="flex items-center">
                      <AccessTime className="mr-1 w-4 h-4 text-main-900" />
                      <span className="text-sm text-gray-600">
                        Outbound:{" "}
                        {pricingInfo.flexiFares.outBoundFlexiAvailable
                          ? "Available"
                          : "Not Available"}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <AccessTime className="mr-1 w-4 h-4 text-main-900" />
                      <span className="text-sm text-gray-600">
                        Inbound:{" "}
                        {pricingInfo.flexiFares.inBoundFlexiAvailable
                          ? "Available"
                          : "Not Available"}
                      </span>
                    </div>
                  </div>

                  {pricingInfo.flexiFares.flexiFares.map((flexiFare, index) => (
                    <div
                      key={index}
                      className="p-4 mb-4 rounded-lg border bg-amber-50/30 border-main-700"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium text-main-900">
                          {flexiFare.flexibilityDescription} -{" "}
                          {flexiFare.applicableJourneyType}
                        </h3>
                        {flexiFare.flexiFareAmount &&
                          flexiFare.flexiFareAmount.amount > 0 && (
                            <span className="text-sm font-medium text-main">
                              +
                              {formatCurrency(
                                flexiFare.flexiFareAmount.amount,
                                flexiFare.flexiFareAmount.currency
                              )}
                            </span>
                          )}
                      </div>

                      <div className="space-y-2">
                        {flexiFare.allowedFlexiOperations?.map(
                          (operation, opIndex) => (
                            <div key={opIndex} className="flex items-start">
                              <CheckCircle className="h-4 w-4 text-main-800 mr-2 mt-0.5 flex-shrink-0" />
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">
                                  {operation.numberOfAllowedOperations}
                                </span>{" "}
                                {operation.allowedOperationName}
                                <div className="mt-1 text-xs text-gray-500">
                                  Cutoff:{" "}
                                  {Math.floor(
                                    operation.flexiOperationCutoverTimeInMinutes /
                                      60
                                  )}
                                  h{" "}
                                  {operation.flexiOperationCutoverTimeInMinutes %
                                    60}
                                  m before departure
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>

                      {flexiFare.flexiRuleCode && (
                        <div className="mt-3 text-xs text-gray-500">
                          Rule Code: {flexiFare.flexiRuleCode}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>

          {/* Price Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <h3 className="flex items-center mb-6 text-xl font-semibold text-gray-900">
                <MonetizationOn className="mr-2 w-5 h-5 text-main-800" />
                Final Price Summary
              </h3>

              <div className="space-y-4 text-black">
                {/* Base Fare */}
                {pricingInfo?.totalFare?.baseFare && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Fare</span>
                    <span className="font-medium">
                      {formatCurrency(
                        pricingInfo.totalFare.baseFare.amount,
                        pricingInfo.totalFare.baseFare.currency
                      )}
                    </span>
                  </div>
                )}

                {/* Bundle Fee */}
                {selectedBundles.map((bundle, index) => (
                  <div className="flex justify-between">
                    <div key={index}>
                      <span className="text-gray-600">
                        {bundle?.bundleName || bundle?.name} Bundle{" "}
                        {index === 0 ? "(Outbound)" : "(Inbound)"}
                      </span>
                    </div>
                    <span className="font-medium text-main-900">
                      +
                      {formatCurrency(
                        bundleFee?.amount ||
                          bundle?.perPaxBundledFee ||
                          bundle?.price,
                        bundleFee?.currency || "ETB"
                      )}
                    </span>
                  </div>
                ))}

                {/* Passenger Fees */}
                {pricingResponse?.pricing?.passengers?.map(
                  (passenger, passengerIndex) => (
                    <div key={passengerIndex}>
                      {passenger.fees?.map((fee, feeIndex) => (
                        <div key={feeIndex} className="flex justify-between">
                          <span className="text-gray-600">
                            {fee.code} - {passenger.passengerType?.code}
                          </span>
                          <span className="font-medium text-main-900">
                            {formatCurrency(fee.amount, fee.currency)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )
                )}

                {/* Taxes & Fees */}
                <div className="flex justify-between">
                  <div>
                    <span className="text-gray-600">Taxes & Charges:</span>
                  </div>
                  <span className="font-medium text-main-900">
                    {pricingResponse?.pricing?.totals?.totalFare?.currency}{" "}
                    {formatNumber(totalTax)}
                  </span>
                </div>

                {/* Selected Ancillaries */}
                <div className="mt-4 space-y-3">
                  {/* Selected Baggage */}
                  {Object.keys(selectedBaggage).length > 0 && (
                    <div>
                      <h5 className="mb-2 text-sm font-medium text-gray-700">
                        Selected Baggage:
                      </h5>
                      <div className="space-y-1">
                        {Object.entries(selectedBaggage).map(
                          ([passengerIndex, segments]) =>
                            Object.entries(segments).map(
                              ([segmentIndex, baggage]) => {
                                const segments =
                                  bundlePricingResp?.pricedItineraries?.[0]
                                    ?.segments || [];
                                const segment = segments[segmentIndex];
                                const segmentPath = segment
                                  ? getSegmentPath(segment)
                                  : `Segment ${parseInt(segmentIndex) + 1}`;

                                return (
                                  <div
                                    key={`baggage-${passengerIndex}-${segmentIndex}`}
                                    className="flex justify-between text-sm"
                                  >
                                    <span className="text-gray-600">
                                      Passenger {parseInt(passengerIndex) + 1} -{" "}
                                      {segmentPath}
                                    </span>
                                    <span className="font-medium text-main-900">
                                      {baggage.baggageDescription}
                                    </span>
                                  </div>
                                );
                              }
                            )
                        )}
                      </div>
                    </div>
                  )}

                  {/* Selected Meals */}
                  {Object.keys(selectedMeals).length > 0 && (
                    <div>
                      <h5 className="mb-2 text-sm font-medium text-gray-700">
                        Selected Meals:
                      </h5>
                      <div className="space-y-1">
                        {Object.entries(selectedMeals).map(
                          ([passengerIndex, segments]) =>
                            Object.entries(segments).map(
                              ([segmentIndex, meal]) => {
                                const segments =
                                  bundlePricingResp?.pricedItineraries?.[0]
                                    ?.segments || [];
                                const segment = segments[segmentIndex];
                                const segmentPath = segment
                                  ? getSegmentPath(segment)
                                  : `Segment ${parseInt(segmentIndex) + 1}`;

                                return (
                                  <div
                                    key={`meal-${passengerIndex}-${segmentIndex}`}
                                    className="flex justify-between text-sm"
                                  >
                                    <span className="text-gray-600">
                                      Passenger {parseInt(passengerIndex) + 1} -{" "}
                                      {segmentPath}
                                    </span>
                                    <span className="font-medium text-main-900">
                                      {meal.mealName}
                                    </span>
                                  </div>
                                );
                              }
                            )
                        )}
                      </div>
                    </div>
                  )}

                  {/* Selected Seats */}
                  {Object.keys(selectedSeats).length > 0 && (
                    <div>
                      <h5 className="mb-2 text-sm font-medium text-gray-700">
                        Selected Seats:
                      </h5>
                      <div className="space-y-1">
                        {Object.entries(selectedSeats).map(
                          ([passengerIndex, segments]) =>
                            Object.entries(segments).map(
                              ([segmentIndex, seat]) => {
                                const segments =
                                  bundlePricingResp?.pricedItineraries?.[0]
                                    ?.segments || [];
                                const segment = segments[segmentIndex];
                                const segmentPath = segment
                                  ? getSegmentPath(segment)
                                  : `Segment ${parseInt(segmentIndex) + 1}`;

                                return (
                                  <div
                                    key={`seat-${passengerIndex}-${segmentIndex}`}
                                    className="flex justify-between text-sm"
                                  >
                                    <span className="text-gray-600">
                                      Passenger {parseInt(passengerIndex) + 1} -{" "}
                                      {segmentPath}
                                    </span>
                                    <span className="font-medium text-main-900">
                                      {seat.rowNumber}
                                      {seat.seatNumber}
                                    </span>
                                  </div>
                                );
                              }
                            )
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <hr />
                <div className="flex justify-between mt-2 space-y-2">
                  <div>
                    <span className="text-gray-600">Service Charge</span>
                  </div>
                  <span className="font-medium text-main-900">
                    {pricingResponse?.pricing?.totals?.totalFare?.currency}{" "}
                    {formatNumber(charges || 0)}
                  </span>
                </div>

                <hr className="border-gray-200" />

                {/* Total */}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">
                    Total Amount
                  </span>
                  <div className="text-right">
                    <div className="text-2xl font-semibold text-main-900">
                      {pricingResponse?.pricing?.totals?.totalFare?.currency}{" "}
                      {formatNumber(totalPrice())}
                    </div>
                  </div>
                </div>

                {/* Proceed to Booking Button */}
                <button
                  onClick={handleProceedToBooking}
                  className="flex justify-center items-center p-2 space-x-2 w-full font-semibold text-white rounded-lg shadow-lg transition-colors duration-200 bg-main-900 hover:bg-main-900 hover:shadow-xl"
                >
                  <span>Continue</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Ancillaries
        isBaggageModalOpen={isBaggageModalOpen}
        setIsBaggageModalOpen={setIsBaggageModalOpen}
        passengers={passengers}
        activePassengerTab={activePassengerTab}
        setActivePassengerTab={setActivePassengerTab}
        baggageDetails={baggageDetails}
        selectedBaggage={selectedBaggage}
        setSelectedBaggage={setSelectedBaggage}
        formatCurrency={formatCurrency}
        isMealModalOpen={isMealModalOpen}
        setIsMealModalOpen={setIsMealModalOpen}
        mealDetails={mealDetails}
        activeMealSegmentTab={activeMealSegmentTab}
        setActiveMealSegmentTab={setActiveMealSegmentTab}
        selectedSeats={selectedSeats}
        setSelectedSeats={setSelectedSeats}
        setActiveSeatTab={setActiveSeatTab}
        selectedMeals={selectedMeals}
        setSelectedMeals={setSelectedMeals}
        isSeatModalOpen={isSeatModalOpen}
        setIsSeatModalOpen={setIsSeatModalOpen}
        autoSelectedSeats={autoSelectedSeats}
        activeSeatTab={activeSeatTab}
        seatDetails={seatDetails}
      />
    </div>
  );
};

export default PriceWithBundle;
